import { firebaseAuth } from '../firebase/auth';

const API_URL = import.meta.env.VITE_API_URL;

// Interfaces para las estadísticas de racha
export interface StreakHistory {
    start: string;
    end: string;
    length: number;
}

export interface CompletionRateByDay {
    day: string;
    count: number;
    percentage: number;
}

export interface StreakStats {
    currentStreak: number;
    longestStreak: number;
    streakLevel: string;
    lastCompletedDate: string | null;
    stats: {
        totalTasksCompleted: number;
        streakHistory: StreakHistory[];
        completionRateByDay: CompletionRateByDay[];
    };
}

export interface StreakResetResponse {
    message: string;
    newStreak: number;
}

export interface StreakFilters {
    timeRange?: 'week' | 'month' | 'year' | 'all';
}

// Datos por defecto para cuando la API devuelve respuestas incompletas
const DEFAULT_STREAK_STATS: StreakStats = {
    currentStreak: 0,
    longestStreak: 0,
    streakLevel: 'Principiante',
    lastCompletedDate: null,
    stats: {
        totalTasksCompleted: 0,
        streakHistory: [],
        completionRateByDay: []
    }
};

class StreakAPI {
    private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
        const idToken = await firebaseAuth.getIdToken();
        const headers = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
            ...options.headers
        };

        try {
            const response = await fetch(`${API_URL}${endpoint}`, {
                ...options,
                headers
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                throw new Error(errorData?.message || 'Request failed');
            }

            return response.json();
        } catch (error) {
            console.error(`API Error (${endpoint}):`, error);
            throw error;
        }
    }

    /**
     * Obtiene las estadísticas de racha del usuario y asegura que la estructura de datos sea correcta
     * @param filters Filtros opcionales como userId y timeRange
     * @returns Datos de las rachas
     */
    async getStreakStats(filters: StreakFilters = {}): Promise<StreakStats> {
        try {
            const queryParams = this.buildQueryParams(filters);
            const response = await this.request<Partial<StreakStats>>(`/api/streaks?${queryParams}`);

            // Asegurar que la estructura de datos sea coherente
            return this.normalizeStreakResponse(response);
        } catch (error) {
            console.error('Error fetching streak stats:', error);
            // Devolver datos predeterminados en caso de error
            return { ...DEFAULT_STREAK_STATS };
        }
    }

    /**
     * Reinicia la racha del usuario
     * @param userId ID opcional del usuario (si no se proporciona, se usa el del token)
     * @returns Confirmación del reinicio
     */
    async resetStreak(userId?: string): Promise<StreakResetResponse> {
        return this.request<StreakResetResponse>('/api/streaks/reset', {
            method: 'POST',
        });
    }

    /**
     * Construye los parámetros de consulta para la URL
     * @param filters Objeto de filtros
     * @returns String de parámetros URL
     */
    private buildQueryParams(filters: StreakFilters): string {
        const params = new URLSearchParams();

        if (filters.timeRange) {
            params.append('timeRange', filters.timeRange);
        }

        return params.toString();
    }

    /**
     * Normaliza la respuesta de la API para asegurar una estructura consistente
     * @param response Respuesta parcial de la API
     * @returns Estructura de datos completa y consistente
     */
    private normalizeStreakResponse(response: Partial<StreakStats>): StreakStats {
        return {
            currentStreak: response.currentStreak ?? DEFAULT_STREAK_STATS.currentStreak,
            longestStreak: response.longestStreak ?? DEFAULT_STREAK_STATS.longestStreak,
            streakLevel: response.streakLevel ?? DEFAULT_STREAK_STATS.streakLevel,
            lastCompletedDate: response.lastCompletedDate ?? DEFAULT_STREAK_STATS.lastCompletedDate,
            stats: {
                totalTasksCompleted: response.stats?.totalTasksCompleted ?? DEFAULT_STREAK_STATS.stats.totalTasksCompleted,
                streakHistory: Array.isArray(response.stats?.streakHistory)
                    ? response.stats.streakHistory
                    : DEFAULT_STREAK_STATS.stats.streakHistory,
                completionRateByDay: Array.isArray(response.stats?.completionRateByDay)
                    ? response.stats.completionRateByDay
                    : DEFAULT_STREAK_STATS.stats.completionRateByDay
            }
        };
    }
}

// Exportar una instancia única del servicio
export const streakAPI = new StreakAPI();